import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Permission } from "@/models/Permission";
import { roleApi } from "@/services/api/organizationApi";
import { useState, useEffect, useRef } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface RolePermissionManagerProps {
  roleId: number;
  onPermissionsChange?: (permissions: number[]) => void;
}

export function RolePermissionManager({ roleId, onPermissionsChange }: RolePermissionManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [initializedPermissions, setInitializedPermissions] = useState(false);
  const savePermissionsRef = useRef<() => Promise<void>>();

  const { data: rolePermissions, isLoading: isLoadingRolePermissions, error: rolePermissionsError } = useQuery({
    queryKey: ['role', roleId, 'permissions'],
    queryFn: () => roleApi.getPermissions(roleId),
    retry: 1,
    enabled: !!roleId
  });

  const { data: allPermissions, isLoading: isLoadingAllPermissions, error: permissionsError } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => roleApi.getAllPermissions()
  });

  useEffect(() => {
    if (rolePermissions && Array.isArray(rolePermissions) && !initializedPermissions) {
      setSelectedPermissions(rolePermissions);
      setInitializedPermissions(true);
    }
  }, [rolePermissions, initializedPermissions]);

  const updatePermissionsMutation = useMutation({
    mutationFn: (permissions: number[]) => roleApi.updatePermissions(roleId, permissions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role', roleId, 'permissions'] });
      toast({
        title: "Permissions updated",
        description: "The role permissions have been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating permissions",
        description: "Failed to update role permissions. Please try again.",
        variant: "destructive",
      });
      console.error('Error updating permissions:', error);
    }
  });

  const handlePermissionToggle = (permissionId: number, checked: boolean) => {
    const newPermissions = checked
      ? [...selectedPermissions, permissionId]
      : selectedPermissions.filter(id => id !== permissionId);
    
    setSelectedPermissions(newPermissions);
    
    if (onPermissionsChange) {
      onPermissionsChange(newPermissions);
    }
  };

  const savePermissions = async () => {
    return updatePermissionsMutation.mutateAsync(selectedPermissions);
  };

  useEffect(() => {
    savePermissionsRef.current = savePermissions;
  }, [selectedPermissions]);

  useEffect(() => {
    if (roleId) {
      if (typeof window !== 'undefined') {
        window[`saveRolePermissions_${roleId}`] = savePermissions;
      }
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        delete window[`saveRolePermissions_${roleId}`];
      }
    };
  }, [roleId, savePermissions]);

  if (isLoadingRolePermissions || isLoadingAllPermissions) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (permissionsError) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Unable to load permissions data</AlertTitle>
        <AlertDescription>
          There was an error loading the permissions list. Please try again later.
          {permissionsError instanceof Error && (
            <div className="mt-2 text-xs">{permissionsError.message}</div>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  const permissionsData = allPermissions || [];
  const hasPermissionsData = Array.isArray(permissionsData) && permissionsData.length > 0;

  if (!hasPermissionsData) {
    return (
      <div className="text-muted-foreground">
        Unable to load permissions data. Please try again later.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 my-4">
      <h4 className="text-sm font-medium mb-2">Permissions</h4>
      
      {rolePermissionsError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unable to load existing role permissions</AlertTitle>
          <AlertDescription>
            This role may be new or there was an error loading its permissions. 
            Changes you make will still be saved when you submit.
          </AlertDescription>
        </Alert>
      )}

      <ScrollArea className="h-[300px] w-full rounded-md border p-4">
        <div className="space-y-4">
          {permissionsData.map((permission: Permission) => (
            <div key={permission.id} className="flex items-center space-x-2">
              <Checkbox
                id={`permission-${permission.id}`}
                checked={selectedPermissions.includes(permission.id)}
                onCheckedChange={(checked) => 
                  handlePermissionToggle(permission.id, checked as boolean)
                }
              />
              <Label htmlFor={`permission-${permission.id}`}>
                {permission.entity_name} - {permission.action}
              </Label>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
